import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { BarChart3, GitCompare, Clock, Eye, TrendingUp, Plus, ExternalLink, Loader2 } from 'lucide-react';
import {
  getCommodityBidMe,
  normalizeCommodityBidMe,
  commodityBidDebugLog,
  kindBadgeClass,
  kindLabel,
} from '@/services/commoditybidIntegration';
import CommodityBidItemDetails from '@/components/customer/CommodityBidItemDetails';
import { COMMODITYBID_CREATE_REQUEST_URL } from '@/config/portalUrls';

const FEATURES = [
  {
    icon: BarChart3,
    title: 'Market-based pricing',
    description: 'Access competitive offers aligned with current market conditions.',
  },
  {
    icon: GitCompare,
    title: 'Structured comparison',
    description: 'Review multiple supplier quotes in one clear view.',
  },
  {
    icon: Clock,
    title: 'Faster decision-making',
    description: 'Receive and evaluate offers without delays.',
  },
  {
    icon: Eye,
    title: 'Better price visibility',
    description: 'Understand pricing trends and make informed decisions.',
  },
];

function formatDateShort(value) {
  if (value == null || value === '') return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function CommodityBidDashboardSection() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const loginEmail = (user?.email || '').trim().toLowerCase();
    commodityBidDebugLog('dashboard load — login email:', loginEmail || '(unknown)');

    (async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const res = await getCommodityBidMe();
        if (cancelled) return;
        commodityBidDebugLog('proxy response', {
          status: res.status,
          statusText: res.statusText,
          data: res.data,
        });
        const normalized = normalizeCommodityBidMe(res.data);
        commodityBidDebugLog('normalized items', normalized.items.length, normalized);
        setItems(normalized.items);
      } catch (err) {
        commodityBidDebugLog('proxy error', {
          message: err?.message,
          status: err?.response?.status,
          data: err?.response?.data,
        });
        if (!cancelled) {
          setLoadError(true);
          setItems([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.email]);

  return (
    <section
      className="relative overflow-hidden rounded-3xl border border-orange-200/60 bg-white shadow-[0_2px_24px_-8px_rgba(234,88,12,0.12)]"
      data-testid="commoditybid-dashboard-section"
    >
      <div className="border-b border-orange-100/80 bg-gradient-to-br from-orange-50/80 via-white to-white px-6 py-7 sm:px-8 sm:py-8">
        <div className="flex items-start gap-4">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-orange-500 text-white shadow-sm shadow-orange-500/25">
            <TrendingUp className="h-6 w-6" strokeWidth={1.75} />
          </span>
          <div>
            <h2 className="font-heading text-lg font-semibold text-slate-900 sm:text-xl">
              CommodityBid — Find the best price
            </h2>
            <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-slate-600">
              Access competitive offers, compare multiple suppliers, and make faster smarter sourcing decisions.
            </p>
          </div>
        </div>

        <div className="mt-7 grid gap-3 sm:grid-cols-2">
          {FEATURES.map(({ icon: Icon, title, description }) => (
            <div
              key={title}
              className="rounded-2xl border border-orange-100/80 bg-white/80 p-4 shadow-sm"
            >
              <div className="mb-2 flex items-center gap-2">
                <Icon className="h-4 w-4 text-orange-500" strokeWidth={1.75} />
                <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
              </div>
              <p className="text-xs leading-relaxed text-slate-500">{description}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="border-t border-slate-100 px-6 py-6 sm:px-8 sm:py-7">
        {loading && (
          <div
            className="flex flex-col items-center gap-3 py-8"
            data-testid="commoditybid-loading"
          >
            <Loader2 className="h-8 w-8 animate-spin text-orange-500" strokeWidth={1.75} />
            <p className="text-sm text-slate-500">Loading CommodityBid data…</p>
          </div>
        )}

        {!loading && loadError && (
          <p
            className="rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-center text-sm text-red-800"
            data-testid="commoditybid-load-error"
          >
            CommodityBid data could not be loaded.
          </p>
        )}

        {!loading && !loadError && items.length === 0 && (
          <div className="flex flex-col items-center py-6 text-center" data-testid="commoditybid-empty">
            <TrendingUp className="mb-3 h-10 w-10 text-orange-200" strokeWidth={1.5} />
            <p className="text-sm font-medium text-slate-700">You don&apos;t have any CommodityBid requests yet.</p>
            {user?.email ? (
              <p className="mt-2 text-xs text-slate-500">
                Matched to login email: <span className="font-medium text-slate-700">{user.email}</span>
              </p>
            ) : null}
            <a
              href={COMMODITYBID_CREATE_REQUEST_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="commoditybid-create-request"
              className="mt-5 inline-flex items-center gap-2 rounded-xl bg-orange-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm shadow-orange-500/25 transition-colors hover:bg-orange-600"
            >
              <Plus className="h-4 w-4" strokeWidth={2} />
              Create a CommodityBid request
              <ExternalLink className="h-3.5 w-3.5 opacity-80" strokeWidth={2} />
            </a>
          </div>
        )}

        {!loading && !loadError && items.length > 0 && (
          <div>
            <p className="mb-3 text-xs font-medium uppercase tracking-wide text-slate-500">Pending bids & open requests</p>
            <ul className="flex flex-col gap-2" data-testid="commoditybid-items">
              {items.map((item, i) => (
                <li
                  key={`${item.kind}-${item.id}-${i}`}
                  className="flex items-center justify-between gap-3 rounded-2xl border border-slate-100 bg-slate-50/50 px-4 py-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${kindBadgeClass(item.kind)}`}
                      >
                        {kindLabel(item.kind)}
                      </span>
                      <p className="truncate text-sm font-medium text-slate-900">{item.title}</p>
                    </div>
                    <p className="mt-0.5 text-xs capitalize text-slate-500">{item.status}</p>
                    <CommodityBidItemDetails item={item} compact />
                  </div>
                  <span className="shrink-0 text-xs text-slate-400">{formatDateShort(item.createdAt)}</span>
                </li>
              ))}
            </ul>
            <a
              href={COMMODITYBID_CREATE_REQUEST_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium text-orange-600 hover:text-orange-700"
            >
              <Plus className="h-3.5 w-3.5" />
              Create a CommodityBid request
              <ExternalLink className="h-3 w-3 opacity-70" />
            </a>
          </div>
        )}
      </div>
    </section>
  );
}
